import { getLastSeenString, getValueSafely } from 'utils/utils';
import { Flyout } from 'components/Flyout/Flyout';

import { Beacon } from 'types/beacon';
import { SeperatorStyle, StyledTable } from '../Styled';
import { useUserRolePermissionStore } from 'contexts/RootStore.context';
import { format } from 'date-fns';
import { colours } from 'styling/colours';
import BatterySpan from './BatterySpan';
import LocationSpan from './LocationSpan';
import AccuracyRangeSpan from './AccuracyRangeSpan';
import LastSeenCell from './LastSeenCell';
import { EddystoneProfile, iBeaconProfile } from 'types/BroadcastProfile';
import useDevice from 'hooks/useDevice';
import { Box } from 'gls';
import { NoOperationSupprtStyle } from 'views/assets/AssetFlyout/styles';
import Button from 'components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { DeleteModal } from './DeleteModal';
import { deleteBeacon } from 'api/beacons.api';

function getBeaconProfileStringFromObj(broadcastProfileObj: Object) {
  return Object.keys(broadcastProfileObj).join(', ');
}

function getCalculatedRSSIValue(broadcastProfile: EddystoneProfile | iBeaconProfile | null) {
  if (!broadcastProfile || !broadcastProfile?.tx_power) return null;

  // if tx_power has length of 2, and includes '-' it must be a tx_value such as '-24'
  if (
    broadcastProfile.tx_power.toString().includes('-') &&
    broadcastProfile.tx_power.toString().length >= 2
  ) {
    return `${broadcastProfile.tx_power}`;
    //otherwise, the tx_power will correspond to the index from the calibrated_rssi array
  } else if (broadcastProfile.calibrated_rssi) {
    return `${broadcastProfile.calibrated_rssi[broadcastProfile.tx_power]}`;
  } else return null;
}

type AssetFlyoutProps = {
  onRefresh: () => void
  onClose: () => void
  beacon: Beacon
}

export const BeaconFlyout = ({ beacon, onClose, onRefresh }: AssetFlyoutProps) => {

  const assetIsOutdoor = beacon?.location?.is_outdoors;
  const { canManageAssets } = useUserRolePermissionStore()
  const { isMobileDevice } = useDevice()
  const navigate = useNavigate()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const beaconProfiles = getBeaconProfileStringFromObj(beacon?.broadcast_profiles || {});

  const createdDate = beacon?.import_info?.import_timestamp
    ? format(new Date(beacon?.import_info?.import_timestamp), 'dd/LL/yyyy')
    : '––';

  const deleteHandler = () => setShowDeleteModal(true)
  const onDelete = (beacon_id: string) => {
    return deleteBeacon(beacon_id).then(() => {
      onRefresh()
      setShowDeleteModal(false)
      onClose()
    })
  }

  return (
    <>
      <Flyout isOpen={beacon !== null} onClose={onClose} headerTitle={'Beacon Details'}
        footer={canManageAssets && (isMobileDevice
          ? (
            <Box horizontalAlign='center' className={NoOperationSupprtStyle}>
              Please use a larger screen to edit or archive assets.
            </Box>
          )
          : (
            <>
              <Button className="biggerButton" onClick={() => { navigate(`/edit-beacon/${beacon?.beacon_id}`); }} id="edit-button">
                Edit
              </Button>
              <Button className="biggerButton" styling='cancel' onClick={deleteHandler} id="delete-button">
                Delete
              </Button>
            </>
          ))}
      >
        <StyledTable data-test-id="beacon-details-general">
          <tbody>
            <tr>
              <td>Beacon ID:</td>
              <td>{getValueSafely(beacon?.beacon_id)}</td>
            </tr>
            <tr>
              <td>Beacon Type:</td>
              <td>{getValueSafely(beacon?.beacon_type)}</td>
            </tr>
          </tbody>
        </StyledTable>
        <div className={SeperatorStyle} />
        <StyledTable data-test-id="beacon-details-protocol">
          <tbody>
            <tr>
              <td>MAC Address:</td>
              <td>{beacon && getValueSafely(beacon?.broadcast_profiles.eddystone_uid?.mac_address)}</td>
            </tr>
            <tr>
              <td>Import Date:</td>
              <td>{createdDate}</td>
            </tr>
            <tr>
              <td>Notes:</td>
              <td>{getValueSafely(beacon?.notes)}</td>
            </tr>

            <tr>
              <td>Broadcast Profiles:</td>
              <td>{getValueSafely(beaconProfiles)}</td>
            </tr>
            <tr>
              <td className="profile-title">Eddystone</td>
            </tr>
            <tr>
              <td>Namespace:</td>
              <td>{getValueSafely(beacon?.broadcast_profiles.eddystone_uid?.namespace)}</td>
            </tr>
            <tr>
              <td>Instance:</td>
              <td>{getValueSafely(beacon?.broadcast_profiles.eddystone_uid?.instance)}</td>
            </tr>
            <tr>
              <td>Transmission Power:</td>
              <td>{beacon && getValueSafely(getCalculatedRSSIValue(beacon?.broadcast_profiles.eddystone_uid), ' RSSI @0m')}</td>
            </tr>
            <tr>
              <td className="profile-title">iBeacon</td>
            </tr>
            <tr>
              <td>Proximity UUID:</td>
              <td>{getValueSafely(beacon?.broadcast_profiles.ibeacon?.proximity_uuid)}</td>
            </tr>
            <tr>
              <td>Major:</td>
              <td>{getValueSafely(beacon?.broadcast_profiles.ibeacon?.major.toString())}</td>
            </tr>
            <tr>
              <td>Minor:</td>
              <td>{getValueSafely(beacon?.broadcast_profiles.ibeacon?.minor.toString())}</td>
            </tr>
            <tr>
              <td>Transmission Power:</td>
              <td>{beacon && getValueSafely(getCalculatedRSSIValue(beacon?.broadcast_profiles.ibeacon), ' RSSI @1m')}</td>
            </tr>
          </tbody>
        </StyledTable>
        <div className={SeperatorStyle} />
        <StyledTable data-test-id="beacon-details-estimate">
          <tbody>
            <tr>
              <td className="profile-title">Beacon estimates:</td>
            </tr>
            <tr>
              <td>Last Seen:</td>
              <td>
                <LastSeenCell timestamp={beacon?.last_location_update_timestamp || ""} />
                {` ${getLastSeenString(beacon?.last_location_update_timestamp || "")}`}
              </td>
            </tr>
            <tr>
              <td>Accuracy Range:</td>
              <td>
                {beacon && <AccuracyRangeSpan beacon={beacon} />}
              </td>
            </tr>
            <tr>
              <td>Status:</td>
              <td style={{ textTransform: 'capitalize' }}>
                {beacon?.mobility_state === 'unknown'
                  ? '––'
                  : beacon?.mobility_state}
              </td>
            </tr>
            <tr>
              <td>Battery:</td>
              <td>
                {beacon && <BatterySpan beacon={beacon} />}
              </td>
            </tr>
            <tr>
              <td valign="top">Location:</td>
              <td>
                {beacon && (
                  assetIsOutdoor
                    ? <span style={{ marginBottom: '10px' }}>Outside Mapped Buildings</span>
                    : <LocationSpan beacon={beacon} />
                )}
                <div style={{ color: colours.secondaryText, margin: '15px 0' }}>Possible region:</div>
                <div>{getValueSafely(beacon?.location?.region?.name)}</div>
              </td>
            </tr>
          </tbody>
        </StyledTable>
        <div className={SeperatorStyle} />
        <StyledTable data-test-id="beacon-details-asset">
          <tbody>
            <tr>
              <td className="profile-title">Assigned asset:</td>
            </tr>
            <tr>
              <td>Asset Name:</td>
              <td>{getValueSafely(beacon?.assigned_asset?.name)}</td>
            </tr>
            <tr>
              <td>Asset Type:</td>
              <td>{getValueSafely(beacon?.assigned_asset?.asset_type.name)}</td>
            </tr>
            <tr>
              <td>Asset ID:</td>
              <td>{getValueSafely(beacon?.assigned_asset?.id)}</td>
            </tr>
          </tbody>
        </StyledTable>
      </Flyout >

      {showDeleteModal && (
        <DeleteModal
          beacon_id={beacon.beacon_id}
          asset_name={beacon.assigned_asset?.name}
          onClose={() => setShowDeleteModal(false)}
          onDelete={onDelete}
        />
      )}
    </>
  );
};



